/**
 * An activity's icon in the library (owner 2026-10-18): the button shows the icon the day plan draws,
 * and opens a grid of the allowed icons. «Automatisk» clears the choice, so the name decides again
 * (activityIcon). The choice in effect is the pressed one. Presentation only: the page saves.
 */
import { T } from '@/i18n';
import { ICONS, activityIcon } from '@/pages/vaktplan/activityIcon';
import { Icon, type IconName } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import s from './IconPick.module.css';

/** What each icon is called, for its button's name. */
const NAMES: Record<string, string> = {
  clipboard: 'Liste',
  utensils: 'Mat',
  door: 'Dør',
  lock: 'Lås',
  store: 'Kasse',
  box: 'Eske',
  truck: 'Levering',
  thermometer: 'Termometer',
  sparkle: 'Rengjøring',
  users: 'Personer',
  user: 'Person',
  chat: 'Melding',
  document: 'Dokument',
  phone: 'Telefon',
  mail: 'E-post',
  calendar: 'Kalender',
  clock: 'Klokke',
  check: 'Hake',
  briefcase: 'Koffert',
  heart: 'Hjerte',
  banknote: 'Penger',
  pin: 'Sted',
  folder: 'Mappe',
  sun: 'Sol',
};
export const iconName = (i: string) => T(NAMES[i] ?? i);

export function IconPick({
  task,
  icon,
  disabled,
  onPick,
}: {
  task: string;
  /** the stored choice, null for automatic */
  icon: string | null | undefined;
  disabled?: boolean;
  onPick: (icon: IconName | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const guess = activityIcon(task);
  const shown = activityIcon(task, false, icon);
  const auto = !icon || !(ICONS as string[]).includes(icon);
  const pick = (v: IconName | null) => {
    setOpen(false);
    if (v !== (icon ?? null)) onPick(v);
  };
  // who may not edit sees the icon in use, not a control
  if (disabled)
    return (
      <span
        className={s.btn}
        role="img"
        aria-label={T('Ikon for {task}: {icon}', { task, icon: iconName(shown) })}
      >
        <Icon name={shown} />
      </span>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={s.btn}
          data-testid="task-icon"
          aria-label={T('Ikon for {task}: {icon}', {
            task,
            icon: icon ? iconName(shown) : `${T('Automatisk')}, ${iconName(guess)}`,
          })}
        >
          <Icon name={shown} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={`pop ${s.pop}`}
          sideOffset={4}
          collisionPadding={8}
          data-testid="task-icon-grid"
        >
          <h6>{T('Ikon for {task}', { task })}</h6>
          <button
            type="button"
            className={s.auto}
            aria-pressed={auto}
            data-testid="task-icon-auto"
            onClick={() => pick(null)}
          >
            <Icon name={guess} />
            <span>
              {T('Automatisk')}
              <small>{T('Ut fra navnet')}</small>
            </span>
          </button>
          <fieldset className={s.grid} aria-label={T('Velg ikon')}>
            {ICONS.map((i) => (
              <button
                type="button"
                key={i}
                className={s.cell}
                aria-pressed={!!icon && icon === i}
                aria-label={iconName(i)}
                title={iconName(i)}
                data-icon={i}
                onClick={() => pick(i)}
              >
                <Icon name={i} />
              </button>
            ))}
          </fieldset>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
